<template>
  <div class="ss-panel">
    <div v-if="!hasContent" class="ss-empty">
      <span>Social scripts not generated yet.</span>
    </div>

    <template v-else>
      <div class="ss-script-list">
        <div v-for="(script, si) in scripts" :key="script.id || ('ss-' + si)" class="ss-script-block">
          <div class="ss-script-meta">
            <span class="ss-script-index">{{ si + 1 }}</span>
            <span v-if="script.title" class="ss-script-title">{{ script.title }}</span>
            <span class="ss-script-session">{{ script.session_ref || '(no session ref)' }}</span>
            <span v-if="script.module_ref" class="ss-script-module">{{ script.module_ref }}</span>
            <span class="ss-word-count">{{ script.word_count || countWords(script) }} words</span>
            <span v-if="script.gold_quote_ref != null" class="ss-gq-ref">gq#{{ script.gold_quote_ref }}</span>
          </div>

          <!-- Visual card preview -->
          <div class="ss-card-row">
            <div class="ss-card" :ref="'ssCard' + si">
              <div class="ss-card-inner">
                <div class="ss-card-bottom-bar">
                  <img v-if="collectionForm.cover_url" :src="collectionForm.cover_url" class="ss-collection-cover" />
                  <div class="ss-brand-tag">
                    <img src="/LearningFirst_logo.png" class="ss-logo-icon" alt="" /> LearningFirst
                  </div>
                </div>
                <div class="ss-card-footer">
                  <div class="ss-collection-title">{{ collectionForm.title }}</div>
                  <div class="ss-card-type-label">Social Script</div>
                </div>
                <div v-if="script.title" class="ss-card-title-text">{{ script.title }}</div>
                <div v-if="script.hook" class="ss-card-hook">{{ script.hook }}</div>
                <div v-if="script.lines && script.lines.length" class="ss-card-lines">
                  <div v-for="(line, li) in script.lines" :key="'l-' + si + '-' + li"
                       class="ss-card-line" :class="{ 'ss-card-line-blank': !line }">
                    {{ line || '' }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Per-script captions -->
          <div v-if="captionsFor(script)" class="ss-captions">
            <div class="ss-captions-toggle" @click="toggleCaptions(script.id || ('ss-' + si))">
              <span>{{ captionsOpen[script.id || ('ss-' + si)] ? '▾' : '▸' }} Per-platform captions ({{ platformCount(script) }})</span>
            </div>
            <div v-if="captionsOpen[script.id || ('ss-' + si)]" class="ss-caption-list">
              <div v-for="platform in platforms" :key="platform" class="ss-caption-item">
                <template v-if="captionRendered(script, platform)">
                  <div class="ss-caption-head">
                    <span class="ss-platform-tag">{{ platform }}</span>
                    <span class="ss-char-count">{{ captionRendered(script, platform).length }} chars</span>
                    <el-button size="mini" type="text" icon="el-icon-document-copy"
                               @click="copy(captionRendered(script, platform), platform)">Copy</el-button>
                  </div>
                  <div class="ss-caption-text">{{ captionRendered(script, platform) }}</div>
                </template>
              </div>
            </div>
          </div>

          <div class="ss-script-actions">
            <el-button size="mini" icon="el-icon-document-copy" @click="copyScript(script)">Copy script</el-button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: "SocialScriptsPanel",
  props: {
    collectionForm: { type: Object, required: true },
    collectionId: { type: String, default: "" },
  },
  data() {
    return {
      platforms: ["x", "instagram", "tiktok", "xiaohongshu", "substack_notes"],
      captionsOpen: {},
    };
  },
  computed: {
    data() {
      return this.collectionForm && this.collectionForm.social_scripts
        ? this.collectionForm.social_scripts
        : null;
    },
    hasContent() {
      return !!(this.data && this.data.scripts && this.data.scripts.length);
    },
    scripts() {
      return (this.data && this.data.scripts) || [];
    },
  },
  methods: {
    countWords(script) {
      if (!script.lines) return 0;
      return script.lines.join(" ").split(/\s+/).filter(Boolean).length;
    },
    captionsFor(script) {
      if (!this.data || !this.data.captions) return null;
      return this.data.captions[script.id] || null;
    },
    platformCount(script) {
      const c = this.captionsFor(script);
      return c ? Object.keys(c).length : 0;
    },
    captionRendered(script, platform) {
      const c = this.captionsFor(script);
      if (!c || !c[platform]) return "";
      return c[platform].rendered || "";
    },
    toggleCaptions(key) {
      this.$set(this.captionsOpen, key, !this.captionsOpen[key]);
    },
    copyScript(script) {
      const text = (script.lines || []).join("\n");
      this.copy(text, "Script");
    },
    copy(text, label) {
      if (!text) return;
      const fallback = () => {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand("copy"); } catch (e) {}
        document.body.removeChild(ta);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
          .then(() => this.$message.success(`${label} copied`))
          .catch(() => { fallback(); this.$message.success(`${label} copied`); });
      } else {
        fallback();
        this.$message.success(`${label} copied`);
      }
    },
  },
};
</script>

<style scoped>
.ss-panel { padding: 10px 4px; }
.ss-empty { padding: 24px; text-align: center; color: #909399; font-style: italic; }

.ss-script-list { display: flex; flex-direction: column; gap: 28px; }

.ss-script-block { border: 1px solid #ebeef5; border-radius: 8px; padding: 16px; }

/* Meta row above the card */
.ss-script-meta { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; flex-wrap: wrap; }
.ss-script-index { display: inline-flex; align-items: center; justify-content: center; width: 24px; height: 24px; border-radius: 50%; background: #409eff; color: #fff; font-size: 12px; font-weight: 600; flex-shrink: 0; }
.ss-script-title { font-weight: 700; font-size: 15px; color: #303133; flex-basis: 100%; }
.ss-script-session { font-weight: 500; font-size: 13px; color: #606266; }
.ss-script-module { font-size: 12px; color: #909399; background: #f4f4f5; padding: 2px 8px; border-radius: 3px; }
.ss-word-count { font-size: 12px; color: #909399; }
.ss-gq-ref { font-size: 11px; color: #409eff; background: #ecf5ff; padding: 2px 6px; border-radius: 3px; }

/* Visual card — matches sns-card pattern */
.ss-card-row { margin-bottom: 14px; }

.ss-card {
  width: 500px;
  aspect-ratio: 5 / 7;
  container-type: inline-size;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  background: linear-gradient(165deg, #f0f7ff 0%, #ffffff 40%, #f5f9ff 100%);
  border: 1px solid #c3d9f5;
}

.ss-card-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 6% 7% 20% 7%;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
}

.ss-card-inner::before {
  content: '';
  position: absolute;
  top: 4%;
  right: -8%;
  width: 50%;
  height: 50%;
  background-image: url('/LearningFirst_logo.png');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: top right;
  opacity: 0.05;
  pointer-events: none;
  z-index: 0;
}

.ss-card-inner > * {
  position: relative;
  z-index: 1;
}

/* Bottom bar with cover + brand */
.ss-card-bottom-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 14%;
  display: flex;
  align-items: center;
  padding: 0 7%;
  z-index: 2;
  background: linear-gradient(0deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.7) 100%);
}

.ss-collection-cover {
  width: 7cqi;
  height: 9.8cqi;
  object-fit: cover;
  border-radius: 1cqi;
  box-shadow: 0 1px 4px rgba(0,0,0,0.12);
}

.ss-brand-tag {
  font-size: 2.8cqi;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #409EFF;
  display: flex;
  align-items: center;
  gap: 1cqi;
  margin-left: auto;
  line-height: 1;
}

.ss-logo-icon {
  width: 3.6cqi;
  height: 3.6cqi;
  object-fit: contain;
}

/* Footer — collection title + type label */
.ss-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 3cqi;
}

.ss-collection-title {
  font-size: 2.4cqi;
  color: #606266;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 60%;
}

.ss-card-type-label {
  font-size: 2.6cqi;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #409EFF;
}

/* Title on card */
.ss-card-title-text {
  font-size: 4cqi;
  font-weight: 700;
  color: #1a2a4a;
  line-height: 1.3;
  margin-bottom: 2cqi;
}

/* Hook */
.ss-card-hook {
  font-size: 3.6cqi;
  font-weight: 600;
  color: #303133;
  line-height: 1.45;
  margin-bottom: 2.5cqi;
  padding-left: 2.5cqi;
  border-left: 0.6cqi solid #409EFF;
}

/* Script lines */
.ss-card-lines {
  flex: 1;
  overflow: hidden;
}

.ss-card-line {
  font-size: 2.8cqi;
  color: #303133;
  line-height: 1.65;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
}

.ss-card-line-blank {
  height: 2cqi;
}

/* Captions section */
.ss-captions { margin-bottom: 10px; }
.ss-captions-toggle { cursor: pointer; font-size: 13px; color: #606266; font-weight: 500; padding: 6px 0; user-select: none; }
.ss-captions-toggle:hover { color: #409eff; }
.ss-caption-list { display: flex; flex-direction: column; gap: 10px; padding-top: 8px; }
.ss-caption-item { padding-left: 12px; }
.ss-caption-head { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
.ss-platform-tag { display: inline-block; padding: 2px 8px; background: #ecf5ff; color: #409eff; border-radius: 3px; font-size: 12px; font-weight: 500; text-transform: capitalize; }
.ss-char-count { font-size: 11px; color: #909399; }
.ss-caption-text { font-size: 13px; color: #303133; line-height: 1.6; white-space: pre-wrap; word-break: break-word; }

.ss-script-actions { display: flex; gap: 8px; padding-top: 6px; border-top: 1px solid #f0f2f5; }
</style>
